import type { ReactElement, ReactNode } from 'react';
import { Box, GlobalStyles } from '@mui/material';
import { APP_SCROLL_CONTAINER_ID } from '@utils/appScroller';

import { useAppContentHeight } from './useAppContentHeight';

// Keep the viewport lock tied to this shell; public document routes and print keep their own flow.
const documentScrollLock = (
  <GlobalStyles styles={{ '@media screen': { 'html, body': { overflow: 'hidden' } } }} />
);

type AppFrameProps = {
  children: ReactNode;
  chrome: ReactNode;
  overlays?: ReactNode;
  backgroundColor?: string;
  /** Workspace routes provide their own bounded scrolling panels. */
  mode?: 'page' | 'workspace';
};

/** Keep app chrome in flow and give route content the remaining viewport height. */
export function AppFrame({
  children,
  chrome,
  overlays,
  backgroundColor = 'transparent',
  mode = 'page',
}: AppFrameProps): ReactElement {
  const contentRef = useAppContentHeight();

  return (
    <Box
      id="root-container"
      className="rr-mask"
      sx={{
        height: '100dvh',
        width: '100%',
        minWidth: 0,
        overflow: 'clip',
        backgroundColor,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {documentScrollLock}
      <Box sx={{ flexShrink: 0, minWidth: 0 }}>{chrome}</Box>
      <Box
        id={APP_SCROLL_CONTAINER_ID}
        ref={contentRef}
        data-app-layout={mode}
        sx={{
          position: 'relative',
          flex: '1 1 0',
          minHeight: 0,
          minWidth: 0,
          width: '100%',
          overflowY: mode === 'page' ? 'auto' : 'hidden',
          overflowX: 'hidden',
        }}
      >
        <Box sx={{ height: '100%', width: '100%', minWidth: 0 }}>{children}</Box>
      </Box>
      {overlays && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            '& > *': { pointerEvents: 'auto' },
          }}
        >
          {overlays}
        </Box>
      )}
    </Box>
  );
}
